import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth, canViewActual, hasRole } from "../lib/auth";
import { PageHeader, Section } from "../components/Primitives";
import { MarginCell, InternalBadge } from "../components/MarginCell";
import MarginTrendChart from "../components/MarginTrendChart";
import Pagination, { usePagination } from "../components/Pagination";
import { Settings2, X, Search, FileDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, fmtMoneyPrecise } from "../lib/money";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import { COUNTRIES } from "../lib/regions";
import { downloadCsv } from "../lib/csv";

const fmtNum = (v) => (v || 0).toLocaleString("en-US");

// Map a stored region value ("Malaysia", "malaysia", "MY", "APAC") → its 2-letter ISO code
// where possible. For region groups (APAC/EMEA/…) we keep the value as-is. For unknown
// strings we uppercase up to 3 chars so we always show something short.
const _codeByName = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]));
const _validCodes = new Set(COUNTRIES.map((c) => c.code));
const regionCode = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (!s) return "";
    const up = s.toUpperCase();
    if (_validCodes.has(up)) return up;                 // already an alpha-2 code
    const byName = _codeByName.get(s.toLowerCase());
    if (byName) return byName;                          // matched full country name
    return up.slice(0, 4);                              // region group (APAC) or free-form label
};

export default function Campaigns() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const loadSeq = useRef(0);
    const apiLoading = useApiLoading();
    const [advertisers, setAdvertisers] = useState([]);
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
    const [filter, setFilter] = useState({ advertiser: "", platform: "", region: "", date_from: monthAgo, date_to: today, has_metrics: false });
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ client_cpm: 0, client_budget: 0, pricing_mode: "CPM", platform_margin_pct: "", notes: "", budget_periods: [] });
    const [confirmPassthrough, setConfirmPassthrough] = useState(null); // { campaign, nextOn: bool }

    const askPassthroughToggle = (c) => {
        // Confirm ALWAYS (per spec) — both ON and OFF transitions deserve a sanity check.
        setConfirmPassthrough({ campaign: c, nextOn: !c.show_actual_spend_to_advertiser });
    };

    const confirmPassthroughChange = async () => {
        if (!confirmPassthrough) return;
        const { campaign, nextOn } = confirmPassthrough;
        try {
            await api.put(`/campaigns/${campaign.id}/passthrough-spend`, { show_actual_spend_to_advertiser: nextOn });
            toast.success(nextOn
                ? `Pass-through enabled — ${campaign.advertiser_name || "advertiser"} will see actual spend for "${campaign.name}"`
                : `Pass-through disabled — advertiser will see CPM-derived display spend for "${campaign.name}"`);
            setConfirmPassthrough(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const load = async () => {
        const params = {};
        if (filter.advertiser) params.advertiser_id = filter.advertiser;
        if (filter.platform) params.platform = filter.platform;
        if (filter.date_from) params.date_from = filter.date_from;
        if (filter.date_to) params.date_to = filter.date_to;
        if (filter.has_metrics) params.has_metrics = "true";
        // Guard against out-of-order responses: rapid filter changes fire concurrent requests,
        // and a slower earlier response must not overwrite a newer one.
        const reqId = ++loadSeq.current;
        const { data } = await api.get("/campaigns", { params });
        if (reqId === loadSeq.current) setList(data);
    };
    useEffect(() => {
        load();
    }, [filter]); // eslint-disable-line
    useEffect(() => {
        if (hasRole(user, "super_admin", "admin"))
            api.get("/advertisers").then((r) => setAdvertisers(r.data));
    }, [user.role]); // eslint-disable-line

    // Region-wise rollup for the summary strip above the filters. Grouped by (ISO code,
    // currency) so campaigns in USD get their OWN card next to the native-currency card —
    // e.g. Malaysia gets "MY · MYR" and "MY · USD" side by side. Card sort: biggest spend first.
    const regionSummary = useMemo(() => {
        const groups = new Map();
        for (const c of list) {
            const code = regionCode(c.region);
            if (!code) continue;
            if (filter.region && code !== filter.region) continue;
            const cur = (c.currency || "USD").toUpperCase();
            const key = `${code}|${cur}`;
            const g = groups.get(key) || {
                key,
                code,
                fullName: c.region,
                currency: cur,
                total_spend: 0,       // display_spend — what the advertiser sees
                total_budget: 0,      // client_budget — total committed
                total_margin: 0,      // display_spend - actual_spend (agency profit)
                campaigns: 0,
            };
            g.total_spend += Number(c.display_spend || 0);
            g.total_budget += Number(c.range_budget != null ? c.range_budget : c.client_budget || 0);
            g.total_margin += Number(c.margin || 0);
            g.campaigns += 1;
            groups.set(key, g);
        }
        return Array.from(groups.values()).sort((a, b) => b.total_spend - a.total_spend);
    }, [list, filter.region]);

    const openEdit = (c) => {
        setEditing(c);
        setForm({
            client_cpm: c.client_cpm,
            pricing_mode: c.pricing_mode || "CPM",
            client_budget: c.client_budget,
            advertiser_id: c.advertiser_id || "",
            platform_margin_pct: (c.platform_margin_pct == null ? "" : String(c.platform_margin_pct)),
            notes: "",
            budget_periods: (c.budget_periods || []).map((p) => ({ date_from: p.date_from, date_to: p.date_to, budget: String(p.budget) })),
        });
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                client_cpm: parseFloat(form.client_cpm),
                pricing_mode: form.pricing_mode || "CPM",
                client_budget: parseFloat(form.client_budget),
                advertiser_id: form.advertiser_id || null,
                notes: form.notes,
                status: "active",
                // Only keep fully-filled rows; send [] to clear all periods.
                budget_periods: (form.budget_periods || [])
                    .filter((p) => p.date_from && p.date_to && p.budget !== "" && p.budget != null)
                    .map((p) => ({ date_from: p.date_from, date_to: p.date_to, budget: parseFloat(p.budget) })),
            };
            // Only super_admin can set the platform margin — backend ignores it for other roles anyway,
            // but we omit it from the payload for cleanliness.
            if (user.role === "super_admin") {
                const pm = form.platform_margin_pct;
                payload.platform_margin_pct = (pm === "" || pm === null || pm === undefined)
                    ? null
                    : parseFloat(pm);
            }
            await api.put(`/campaigns/${editing.id}/commercial`, payload);
            toast.success("Commercial settings saved");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const showInternal = canViewActual(user);
    const canEdit = hasRole(user, "super_admin", "admin");

    // Region options for the market filter — built from whatever regions the loaded campaigns carry.
    const regionOptions = useMemo(() => {
        const map = {};
        for (const c of list) {
            const code = regionCode(c.region);
            if (code && !map[code]) map[code] = c.region;
        }
        return Object.entries(map)
            .map(([code, label]) => ({ code, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [list]);

    const searchFiltered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return list.filter((c) => {
            if (filter.region && regionCode(c.region) !== filter.region) return false;
            if (!s) return true;
            return (
                (c.name || "").toLowerCase().includes(s) ||
                (c.advertiser_name || "").toLowerCase().includes(s) ||
                (c.objective || "").toLowerCase().includes(s)
            );
        });
    }, [list, search, filter.region]);

    const pager = usePagination(searchFiltered, 50);

    const exportCsv = () => {
        if (!searchFiltered.length) { toast.info("Nothing to export"); return; }
        // Column order mirrors the on-screen table. Internal-only columns are included when the
        // viewer can see them. Currency is per-row (the ad account's currency).
        const headers = [
            { label: "Campaign", key: (r) => r.name || "" },
            { label: "Campaign ID", key: (r) => r.external_campaign_id || "" },
            { label: "Platform", key: (r) => r.platform || "" },
            { label: "Region", key: (r) => regionCode(r.region) || "" },
            ...(showInternal ? [{ label: "Advertiser", key: (r) => r.advertiser_name || "" }] : []),
            { label: "Impressions", key: (r) => r.impressions || 0 },
            { label: "Clicks", key: (r) => r.clicks || 0 },
            { label: "Views", key: (r) => r.views || 0 },
            { label: "Unit Cost", key: (r) => fmtMoneyPrecise(r.client_cpm, r.currency || "USD") },
            ...(showInternal ? [{ label: "Budget", key: (r) => fmtMoney(r.range_budget != null ? r.range_budget : r.client_budget, r.currency || "USD") }] : []),
            { label: "Campaign Budget", key: (r) => fmtMoney(r.display_spend, r.currency || "USD") },
            ...(showInternal ? [
                { label: "Campaign Spends", key: (r) => fmtMoney(r.actual_spend, r.currency || "USD") },
                { label: "Margin", key: (r) => fmtMoney(r.margin, r.currency || "USD") },
                { label: "Margin %", key: (r) => (r.margin_pct != null ? r.margin_pct.toFixed(2) : "") },
            ] : []),
            { label: "Currency", key: (r) => r.currency || "USD" },
        ];
        const stamp = new Date().toISOString().slice(0, 10);
        downloadCsv(`campaigns-${stamp}.csv`, headers, searchFiltered);
        toast.success(`Downloaded ${searchFiltered.length} row${searchFiltered.length === 1 ? "" : "s"}`);
    };

    return (
        <div data-testid="campaigns-page">
            <PageHeader
                eyebrow={showInternal ? "Internal view" : "Commercial view"}
                title="Campaigns"
                subtitle={
                    showInternal
                        ? "Edit client unit cost and budget. Margin (amber) is hidden from advertisers."
                        : "Performance metrics + your committed commercial layer."
                }
                actions={
                    <button
                        onClick={exportCsv}
                        disabled={!searchFiltered.length}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-700"
                        data-testid="campaigns-export-csv"
                        title={searchFiltered.length ? `Download ${searchFiltered.length} filtered row${searchFiltered.length === 1 ? "" : "s"} as CSV` : "Nothing to export"}
                    >
                        <FileDown className="h-4 w-4" />Download CSV
                    </button>
                }
            />

            {/* Region-wise rollup — appears when at least one campaign in the current filter
                has a region tag. Grouped by ISO country code (MY, IN, SG …). */}
            {regionSummary.length > 0 && (
                <div className="mb-4" data-testid="region-summary-strip">
                    <div className="overline mb-2">By region</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {regionSummary.map((g) => (
                            <div key={g.key} className="bg-white border border-zinc-200 rounded p-3" data-testid={`region-summary-${g.code}-${g.currency}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono uppercase bg-zinc-100 text-zinc-800" title={g.fullName}>{g.code}</span>
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase bg-zinc-50 border border-zinc-200 text-zinc-600">{g.currency}</span>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 tabular-nums">{g.campaigns} camp{g.campaigns === 1 ? "" : "s"}</span>
                                </div>
                                <div className="grid grid-cols-1 gap-1.5">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total Spend</div>
                                        <div className="font-display text-base tabular-nums text-zinc-900">{fmtMoney(g.total_spend, g.currency)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total Budget</div>
                                        <div className="font-display text-base tabular-nums text-zinc-700">{fmtMoney(g.total_budget, g.currency)}</div>
                                    </div>
                                    {showInternal && (
                                        <div>
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Margin <InternalBadge /></div>
                                            <div className="font-display text-base tabular-nums" style={{ color: g.total_margin >= 0 ? "var(--wl-primary-hex)" : "#ef4444" }}>{fmtMoney(g.total_margin, g.currency)}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-3 mb-3">
                {hasRole(user, "super_admin", "admin") && (
                    <select value={filter.advertiser} onChange={(e) => setFilter({ ...filter, advertiser: e.target.value })} className="px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="filter-advertiser">
                        <option value="">All advertisers</option>
                        {advertisers.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                    </select>
                )}
                <select value={filter.platform} onChange={(e) => {
                    const platform = e.target.value;
                    setFilter((prev) => ({
                        ...prev,
                        platform,
                        // DV360 doesn't have insights synced yet (Phase 2c) — auto-uncheck "show only with data"
                        // so newly-synced DV360 campaigns are visible. Re-tick for Meta.
                        has_metrics: platform === "dv360" ? false : prev.has_metrics,
                    }));
                }} className="px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="filter-platform">
                    <option value="">All platforms</option>
                    <option value="meta">Meta</option>
                    <option value="dv360">DV360</option>
                    <option value="tiktok">TikTok</option>
                </select>
                <select value={filter.region} onChange={(e) => setFilter({ ...filter, region: e.target.value })} className="px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="filter-region">
                    <option value="">All regions</option>
                    {regionOptions.map((r) => (<option key={r.code} value={r.code}>{r.label} ({r.code})</option>))}
                </select>
                <input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} className="px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="campaigns-date-from" />
                <input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} className="px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="campaigns-date-to" />
                <label className="inline-flex items-center gap-2 text-xs text-zinc-700 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded cursor-pointer">
                    <input type="checkbox" checked={filter.has_metrics} onChange={(e) => setFilter({ ...filter, has_metrics: e.target.checked })} data-testid="filter-has-metrics" />
                    Show only campaigns with data
                </label>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
                    <input
                        type="text"
                        placeholder="Search by campaign or advertiser…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm"
                        data-testid="campaigns-search-input"
                    />
                </div>
                <span className="text-xs text-zinc-500 tabular-nums" data-testid="campaigns-row-count">
                    {searchFiltered.length}
                    {searchFiltered.length !== list.length && (
                        <> <span className="text-zinc-400">of {list.length}</span></>
                    )} campaigns
                </span>
            </div>

            {showInternal && (
                <MarginTrendChart
                    filter={{
                        date_from: filter.date_from,
                        date_to: filter.date_to,
                        advertiser_id: filter.advertiser,
                        platform: filter.platform,
                    }}
                    threshold={30}
                />
            )}

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3">Campaign</th>
                            <th className="overline text-left p-3">Campaign ID</th>
                            <th className="overline text-left p-3">Platform</th>
                            <th className="overline text-left p-3">Region</th>
                            {showInternal && <th className="overline text-left p-3">Advertiser</th>}
                            <th className="overline text-right p-3">Impressions</th>
                            <th className="overline text-right p-3">Clicks</th>
                            <th className="overline text-right p-3">Views</th>
                            <th className="overline text-right p-3">Unit Cost / Mode</th>
                            {showInternal && <th className="overline text-right p-3">Budget</th>}
                            <th className="overline text-right p-3">Campaign Budget</th>
                            {showInternal && <th className="overline text-right p-3 whitespace-nowrap">Campaign Spends <InternalBadge /></th>}
                            {showInternal && <th className="overline text-right p-3 whitespace-nowrap">Margin <InternalBadge /></th>}
                            {canEdit && <th className="overline text-center p-3 whitespace-nowrap" title="When ON, the advertiser sees Meta's actual spend instead of the Unit-Cost-derived display spend">Pass-through</th>}
                            {canEdit && <th className="overline text-right p-3">Edit</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {pager.slice.map((c) => {
                            const cap = c.client_budget > 0 && c.display_spend >= c.client_budget;
                            const cur = c.currency || "USD";
                            return (
                                <tr key={c.id} className="border-b border-zinc-200 last:border-0 hover:bg-zinc-50">
                                    <td className="p-3">
                                        <div className="text-zinc-900 truncate max-w-[260px]" title={c.name}>{c.name}</div>
                                        <div className="text-xs text-zinc-500">{c.objective || c.ad_account_name || ""}</div>
                                    </td>
                                    <td className="p-3 text-zinc-500 text-xs font-mono" title={`Meta/Google ID: ${c.external_campaign_id || "—"}`}>{c.external_campaign_id || "—"}</td>
                                    <td className="p-3 capitalize text-zinc-700">{c.platform}</td>
                                    <td className="p-3" data-testid={`campaign-region-${c.id}`}>
                                        {c.region ? (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono uppercase bg-zinc-100 text-zinc-700"
                                                title={c.region}
                                            >
                                                {regionCode(c.region)}
                                            </span>
                                        ) : <span className="text-zinc-400">—</span>}
                                    </td>
                                    {showInternal && <td className="p-3 text-zinc-700">{c.advertiser_name || "—"}</td>}
                                    <td className="p-3 text-right tabular-nums text-zinc-700">{fmtNum(c.impressions)}</td>
                                    <td className="p-3 text-right tabular-nums text-zinc-700">{fmtNum(c.clicks)}</td>
                                    <td className="p-3 text-right tabular-nums text-zinc-700" data-testid={`campaign-views-${c.id}`}>{fmtNum(c.views)}</td>
                                    <td className="p-3 text-right tabular-nums text-zinc-900" data-testid={`unit-cost-${c.id}`}>{fmtMoneyPrecise(c.client_cpm, cur)}<span className="ml-1 text-[9px] uppercase tracking-wider text-zinc-400">{c.pricing_mode || "CPM"}</span></td>
                                    {showInternal && <td className="p-3 text-right tabular-nums text-zinc-900" data-testid={`campaign-budget-${c.id}`}>{fmtMoney(c.range_budget != null ? c.range_budget : c.client_budget, cur)}{c.budget_periods && c.budget_periods.length > 0 && (<span className="ml-1 text-[9px] uppercase tracking-wider text-zinc-400" title={`${c.budget_periods.length} budget period(s) · prorated to filter range`}>· {c.budget_periods.length}p</span>)}</td>}
                                    <td className="p-3 text-right">
                                        <div className="flex flex-col items-end">
                                            <span className={`tabular-nums font-medium ${cap ? "text-red-400" : "wl-accent-text"}`}>{fmtMoney(c.display_spend, cur)}</span>
                                            {showInternal && (
                                                <div className="w-28 h-1 bg-zinc-200 rounded-full mt-1 overflow-hidden">
                                                    <div
                                                        className="h-full transition-all"
                                                        style={{ width: `${c.client_budget ? Math.min(100, (c.display_spend / c.client_budget) * 100) : 0}%`, background: cap ? "#ef4444" : "var(--wl-primary-hex)" }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {showInternal && <td className="p-3 text-right tabular-nums text-zinc-500">{fmtMoney(c.actual_spend, cur)}</td>}
                                    {showInternal && (
                                        <td className="p-3 text-right">
                                            <MarginCell amount={c.margin} pct={c.margin_pct} currency={cur} dataTestId={`margin-${c.id}`} />
                                        </td>
                                    )}
                                    {canEdit && (
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={() => askPassthroughToggle(c)}
                                                role="switch"
                                                aria-checked={!!c.show_actual_spend_to_advertiser}
                                                title={c.show_actual_spend_to_advertiser
                                                    ? "ON — advertiser sees Meta's actual spend"
                                                    : "OFF — advertiser sees CPM-derived display spend"}
                                                className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors ${c.show_actual_spend_to_advertiser ? "wl-accent-bg" : "bg-zinc-300"}`}
                                                data-testid={`passthrough-toggle-${c.id}`}
                                            >
                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${c.show_actual_spend_to_advertiser ? "translate-x-4" : "translate-x-0.5"}`} />
                                            </button>
                                        </td>
                                    )}
                                    {canEdit && (
                                        <td className="p-3 text-right">
                                            <button onClick={() => openEdit(c)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:wl-accent-text" data-testid={`edit-${c.id}`}>
                                                <Settings2 className="h-3 w-3" />edit
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {!searchFiltered.length && (
                            apiLoading ? <LoadingRow colSpan={14} /> :
                            <tr><td colSpan={14} className="p-10 text-center text-zinc-500">
                                {list.length === 0
                                    ? (filter.has_metrics
                                        ? "No campaigns with metrics in this date range. Try unchecking 'Show only campaigns with data' or extending the range."
                                        : "No campaigns match the current filters.")
                                    : `No campaigns match "${search}".`}
                            </td></tr>
                        )}
                    </tbody>
                </table>
                {searchFiltered.length > 0 && (
                    <Pagination
                        page={pager.page}
                        totalPages={pager.totalPages}
                        pageSize={pager.pageSize}
                        range={pager.range}
                        setPage={pager.setPage}
                        setPageSize={pager.setPageSize}
                        dataTestId="campaigns-pagination"
                    />
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-zinc-50 border border-zinc-200 w-full max-w-md p-6 rounded-md" data-testid="commercial-edit-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl">Commercial settings</h3>
                            <button type="button" onClick={() => setEditing(null)}><X className="h-4 w-4 text-zinc-500" /></button>
                        </div>
                        <div className="text-xs text-zinc-500 mb-4">
                            {editing.name}
                            <div className="inline-flex items-center gap-2 mt-1">
                                <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-200 text-zinc-700">{editing.currency || "USD"}</span>
                                {editing.ad_account_name && (<span className="text-[10px] text-zinc-500">{editing.ad_account_name}</span>)}
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="overline">Pricing mode</label>
                                <select
                                    value={form.pricing_mode}
                                    onChange={(e) => setForm({ ...form, pricing_mode: e.target.value })}
                                    className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm"
                                    data-testid="commercial-mode-select"
                                >
                                    <option value="CPM">CPM — cost per 1,000 impressions</option>
                                    <option value="CPC">CPC — cost per click</option>
                                    <option value="CPV">CPV — cost per view</option>
                                    <option value="CPCV">CPCV — cost per completed view</option>
                                </select>
                                {(form.pricing_mode === "CPV" || form.pricing_mode === "CPCV") && (
                                    <p className="text-[11px] text-amber-600 mt-1" data-testid="mode-coming-soon-note">
                                        {form.pricing_mode} view metrics aren't wired up yet — budget will compute as 0 until enabled. Use CPM or CPC for live billing.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="overline">Rate ({form.pricing_mode}) ({editing.currency || "USD"})</label>
                                <input type="number" step="0.01" value={form.client_cpm} onChange={(e) => setForm({ ...form, client_cpm: e.target.value })} required className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded tabular-nums" data-testid="commercial-cpm-input" />
                                <p className="text-xs text-zinc-500 mt-1">
                                    {form.pricing_mode === "CPM" && "Advertiser spend = Rate × impressions ÷ 1,000."}
                                    {form.pricing_mode === "CPC" && "Advertiser spend = Rate × clicks."}
                                    {form.pricing_mode === "CPV" && "Advertiser spend = Rate × views."}
                                    {form.pricing_mode === "CPCV" && "Advertiser spend = Rate × completed views."}
                                    {" "}Capped at the budget below.
                                </p>
                            </div>
                            <div>
                                <label className="overline">Budget ({editing.currency || "USD"})</label>
                                <input type="number" step="0.01" value={form.client_budget} onChange={(e) => setForm({ ...form, client_budget: e.target.value })} required className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded tabular-nums" data-testid="commercial-budget-input" />
                                <p className="text-xs text-zinc-500 mt-1">Campaign budget will be capped at this value (in {editing.currency || "USD"}) for the advertiser.</p>
                            </div>
                            <div className="border border-zinc-200 rounded p-3 bg-zinc-100/50" data-testid="budget-periods-section">
                                <div className="flex items-center justify-between">
                                    <label className="overline">Budget periods (optional)</label>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, budget_periods: [...(form.budget_periods || []), { date_from: filter.date_from, date_to: filter.date_to, budget: "" }] })}
                                        className="inline-flex items-center gap-1 text-xs wl-accent-text hover:opacity-80"
                                        data-testid="add-budget-period-btn"
                                    >
                                        <Plus className="h-3 w-3" /> Add period
                                    </button>
                                </div>
                                <p className="text-[11px] text-zinc-500 mt-1">
                                    Set date-range budgets. When a date filter is applied, each period is prorated by its overlapping days and summed into the shown budget. Falls back to the single Budget above when no periods are set.
                                </p>
                                {(form.budget_periods || []).length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {form.budget_periods.map((p, idx) => (
                                            <div key={idx} className="flex items-center gap-2" data-testid={`budget-period-row-${idx}`}>
                                                <input
                                                    type="date"
                                                    value={p.date_from || ""}
                                                    onChange={(e) => {
                                                        const next = [...form.budget_periods];
                                                        next[idx] = { ...next[idx], date_from: e.target.value };
                                                        setForm({ ...form, budget_periods: next });
                                                    }}
                                                    className="flex-1 min-w-0 px-2 py-1.5 bg-white border border-zinc-200 rounded text-xs"
                                                    data-testid={`budget-period-from-${idx}`}
                                                />
                                                <span className="text-zinc-400 text-xs">→</span>
                                                <input
                                                    type="date"
                                                    value={p.date_to || ""}
                                                    onChange={(e) => {
                                                        const next = [...form.budget_periods];
                                                        next[idx] = { ...next[idx], date_to: e.target.value };
                                                        setForm({ ...form, budget_periods: next });
                                                    }}
                                                    className="flex-1 min-w-0 px-2 py-1.5 bg-white border border-zinc-200 rounded text-xs"
                                                    data-testid={`budget-period-to-${idx}`}
                                                />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Budget"
                                                    value={p.budget}
                                                    onChange={(e) => {
                                                        const next = [...form.budget_periods];
                                                        next[idx] = { ...next[idx], budget: e.target.value };
                                                        setForm({ ...form, budget_periods: next });
                                                    }}
                                                    className="w-24 px-2 py-1.5 bg-white border border-zinc-200 rounded text-xs tabular-nums"
                                                    data-testid={`budget-period-amount-${idx}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setForm({ ...form, budget_periods: form.budget_periods.filter((_, i) => i !== idx) })}
                                                    className="text-zinc-400 hover:text-red-500"
                                                    data-testid={`remove-budget-period-${idx}`}
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="overline">Advertiser override (optional)</label>
                                <select value={form.advertiser_id} onChange={(e) => setForm({ ...form, advertiser_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                    <option value="">— Use ad account assignment —</option>
                                    {advertisers.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                </select>
                            </div>
                            {user.role === "super_admin" && (
                                <div>
                                    <label className="overline">Platform margin %</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="99.99"
                                        step="0.01"
                                        value={form.platform_margin_pct}
                                        onChange={(e) => setForm({ ...form, platform_margin_pct: e.target.value })}
                                        placeholder="e.g. 15 (leave blank for 0)"
                                        className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded tabular-nums"
                                        data-testid="commercial-margin-input"
                                    />
                                    <p className="text-xs text-zinc-500 mt-1">Markup applied to campaign spends before the agency&apos;s admins / management see it for this campaign. Super-admin only.</p>
                                </div>
                            )}
                            <div>
                                <label className="overline">Internal notes</label>
                                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" rows={2} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-sm text-zinc-500">Cancel</button>
                            <button type="submit" className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="commercial-save-btn">Save</button>
                        </div>
                    </form>
                </div>
            )}

            {confirmPassthrough && (() => {
                const { campaign, nextOn } = confirmPassthrough;
                const advLabel = campaign.advertiser_name || "the advertiser";
                return (
                    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setConfirmPassthrough(null)}>
                        <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-200 w-full max-w-md p-6 rounded-md" data-testid="passthrough-confirm-modal">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-lg">{nextOn ? "Enable pass-through spend?" : "Disable pass-through spend?"}</h3>
                                <button type="button" onClick={() => setConfirmPassthrough(null)} className="text-zinc-400 hover:text-zinc-900"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="text-sm text-zinc-700 space-y-3">
                                <p>
                                    <span className="font-medium">{campaign.name}</span>
                                </p>
                                {nextOn ? (
                                    <p>
                                        From now on, <span className="font-medium">{advLabel}</span> will see Meta&apos;s
                                        <span className="font-medium"> actual spend</span> for this campaign instead of the CPM-derived display spend.
                                        Internal-only metrics (client CPM, margin, margin %) remain hidden from them.
                                    </p>
                                ) : (
                                    <p>
                                        From now on, <span className="font-medium">{advLabel}</span> will see the
                                        <span className="font-medium"> CPM-derived display spend</span> (impressions × client CPM ÷ 1000) for this campaign again.
                                    </p>
                                )}
                                {nextOn && (campaign.client_cpm > 0) && (
                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
                                        Heads up: this campaign has a configured CPM ({campaign.client_cpm}). Enabling pass-through bypasses that — the advertiser&apos;s billable line will change.
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-end gap-2 mt-5">
                                <button type="button" onClick={() => setConfirmPassthrough(null)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="passthrough-cancel-btn">Cancel</button>
                                <button
                                    type="button"
                                    onClick={confirmPassthroughChange}
                                    className="px-4 py-2 rounded text-sm font-medium"
                                    style={{ background: nextOn ? "var(--wl-primary-hex)" : "#dc2626", color: "#ffffff" }}
                                    data-testid="passthrough-confirm-btn"
                                >
                                    {nextOn ? "Enable pass-through" : "Disable pass-through"}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
